import { get, post } from './api';
import {
  MembershipInfo,
  MembershipConfig,
  DurationPackageConfig,
  DurationConsumptionRecord,
  QuotaInfo,
  PurchaseMembershipRequest,
  PurchaseDurationPackageRequest,
  PurchaseOrderResponse,
  CanCreateTaskResponse,
  RecommendedAsrModeResponse,
} from '../types';

/**
 * 会员服务 API
 * 对应后端 MembershipController
 */

// 获取当前用户会员信息
export async function getMembershipInfo(): Promise<MembershipInfo> {
  const response = await get<MembershipInfo>('/api/membership/info');
  return response.data;
}

// 获取会员配置列表
export async function getMembershipConfigs(): Promise<MembershipConfig[]> {
  const response = await get<MembershipConfig[]>('/api/membership/configs');
  return response.data;
}

// 获取时长包配置列表
export async function getDurationPackages(): Promise<DurationPackageConfig[]> {
  const response = await get<DurationPackageConfig[]>('/api/membership/duration-packages');
  return response.data;
}

// 购买会员
export async function purchaseMembership(
  request: PurchaseMembershipRequest
): Promise<PurchaseOrderResponse> {
  const response = await post<PurchaseOrderResponse>('/api/membership/purchase', request);
  return response.data;
}

// 会员购买回调（模拟支付成功）
export async function confirmMembershipPurchase(orderNo: string): Promise<boolean> {
  const response = await post<boolean>('/api/membership/purchase/callback', { orderNo });
  return response.data;
}

// 购买时长包
export async function purchaseDurationPackage(
  request: PurchaseDurationPackageRequest
): Promise<PurchaseOrderResponse> {
  const response = await post<PurchaseOrderResponse>(
    '/api/membership/duration-package/purchase',
    request
  );
  return response.data;
}

// 时长包购买回调（模拟支付成功）
export async function confirmDurationPackagePurchase(orderNo: string): Promise<boolean> {
  const response = await post<boolean>('/api/membership/duration-package/callback', { orderNo });
  return response.data;
}

// 获取时长余额
export async function getQuota(): Promise<QuotaInfo> {
  const response = await get<QuotaInfo>('/api/membership/quota');
  return response.data;
}

// 获取时长消费记录
export async function getDurationRecords(
  page: number = 1,
  pageSize: number = 20
): Promise<{ records: DurationConsumptionRecord[]; total: number }> {
  const response = await get<{ records: DurationConsumptionRecord[]; total: number }>(
    `/api/membership/duration-records?page=${page}&pageSize=${pageSize}`
  );
  return response.data;
}

// 检查是否可以创建任务
export async function canCreateTask(): Promise<CanCreateTaskResponse> {
  const response = await get<CanCreateTaskResponse>('/api/membership/can-create-task');
  return response.data;
}

// 获取推荐的ASR模式
export async function getRecommendedAsrMode(): Promise<RecommendedAsrModeResponse> {
  const response = await get<RecommendedAsrModeResponse>('/api/membership/asr-mode');
  return response.data;
}

// 获取支付地区信息（根据IP判断国内/国外）
export interface PaymentRegionInfo {
  region: 'CN' | 'OVERSEAS';
  country: string;
  city: string;
  ip: string;
  availablePaymentMethods: string[];
}

export async function getPaymentRegion(): Promise<PaymentRegionInfo> {
  const response = await get<PaymentRegionInfo>('/api/membership/payment-region');
  return response.data;
}

// ==========================================
// 辅助函数
// ==========================================

// 格式化时长显示
export function formatMinutes(minutes: number): string {
  return `${minutes}min`;
}

// 格式化时长显示（英文）
export function formatMinutesEn(minutes: number): string {
  return `${minutes}min`;
}

// 计算文本对应的时长（分钟）
export function calculateTextMinutes(charCount: number): number {
  return Math.ceil(charCount / 1000);
}

// 计算音频对应的时长（分钟）
export function calculateAudioMinutes(durationSeconds: number): number {
  return Math.ceil(durationSeconds / 60);
}

// 获取用户等级显示名称
export function getUserLevelName(level: string, lang: 'zh' | 'en' = 'zh'): string {
  const names: Record<string, { zh: string; en: string }> = {
    free: { zh: '免费版', en: 'Free' },
    pro: { zh: 'Pro会员', en: 'Pro' },
    team: { zh: 'Team会员', en: 'Team' },
  };
  return names[level]?.[lang] || level;
}

// 计算会员剩余天数
export function calculateDaysRemaining(membershipMonths: number, lastRefreshAt: string | null): number {
  if (membershipMonths <= 0 || !lastRefreshAt) {
    return 0;
  }
  const lastRefresh = new Date(lastRefreshAt);
  const nextRefresh = new Date(lastRefresh);
  nextRefresh.setDate(nextRefresh.getDate() + 30);

  const now = new Date();
  const daysUntilNextRefresh = Math.ceil((nextRefresh.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

  // 总剩余天数 = 当前周期剩余天数 + (剩余月数 - 1) * 30
  return Math.max(0, daysUntilNextRefresh + (membershipMonths - 1) * 30);
}

// 计算会员到期日期
export function calculateExpiryDate(membershipMonths: number, lastRefreshAt: string | null): Date | null {
  if (membershipMonths <= 0 || !lastRefreshAt) {
    return null;
  }
  const lastRefresh = new Date(lastRefreshAt);
  const expiryDate = new Date(lastRefresh);
  expiryDate.setDate(expiryDate.getDate() + membershipMonths * 30);
  return expiryDate;
}

// 计算免费配额刷新剩余天数
export function calculateFreeQuotaDaysRemaining(lastFreeQuotaRefreshAt: string | null, createdAt?: string | null): number {
  const baseDate = lastFreeQuotaRefreshAt || createdAt;
  if (!baseDate) return 0;
  const base = new Date(baseDate);
  const nextRefresh = new Date(base);
  nextRefresh.setDate(nextRefresh.getDate() + 30);
  const now = new Date();
  return Math.max(0, Math.ceil((nextRefresh.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
}

// 格式化日期
export function formatDate(dateStr: string | null, lang: 'zh' | 'en' = 'zh'): string {
  if (!dateStr) return lang === 'zh' ? '无' : 'N/A';
  const date = new Date(dateStr);
  if (lang === 'zh') {
    return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
  }
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}
